import Moment from 'moment';

import Ajax from 'details/utils/ajax';
import WindowManager from 'details/utils/window_manager';

export class Publication {
    constructor(metadata = {}) {
        // Status fields
        this.citationsLoaded = false;
        this.metricsLoaded = false;

        this.dimensionsRoot = null;

        // Fields supplied by metadata API request
        this.id = metadata.id;
        this.doi = metadata.doi;
        this.pmid = metadata.pmid;
        this.title = metadata.title;
        this.journal = metadata.journal ? new Journal(metadata.journal) : null;
        this.authorNames = metadata.author_names;
        this.publicationDate = metadata.date ? Moment(metadata.date) : null;
        this.timesCited = metadata.times_cited;

        // Fields supplied when this publication is a citation
        this.abstract = metadata.abstract;

        // Fields supplied by citations API request
        this.citations = metadata.citations || null;
        this.citationsYearlyAggregation = metadata.citationsYearlyAggregation || null;
        this.researchCategories = metadata.researchCategories || null;

        // Fields supplied by metrics API
        this.totalCitations = metadata.totalCitations || null;
        this.recentCitations = metadata.recentCitations || null;
        this.relativeCitationRatio = metadata.relativeCitationRatio || null;
        this.fieldCitationRatio = metadata.fieldCitationRatio || null;
    }

    fetchCitations = async path => {
        let data = await Ajax.fetch(path);
        let json = await data.json();
        this.citationsLoaded = true;

        if (!json) return;

        this.citations = (json.publications || []).map(data => new Publication(data));
        this.researchCategories = (json.categories || []).map(data => new ResearchCategory(data));
        this.citationsYearlyAggregation = json.years || [];
    };

    fetchMetrics = async path => {
        let data = await Ajax.fetch(path);
        let json = await data.json();

        this.metricsLoaded = true;

        if (!json) return;

        this.totalCitations = json.times_cited;
        this.recentCitations = json.recent_citations;
        this.relativeCitationRatio = json.relative_citation_ratio;
        this.fieldCitationRatio = json.field_citation_ratio;
    };

    get dimensionsURI() {
        return `${this.dimensionsRoot}/details/publication/${this.id}`;
    }

    get doiURI() {
        return this.doi ? `https://doi.org/${this.doi}` : null;
    }

    get pubmedURI() {
        return this.pmid ? `https://www.ncbi.nlm.nih.gov/pubmed/${this.pmid}` : null;
    }
}

class Journal {
    constructor(data) {
        this.title = data.title;
        this.id = data.id;
    }
}

class ResearchCategory {
    constructor(data) {
        this.id = data.id;
        this.name = data.name;
        this.count = data.count;
    }
}

// Load the basic publication data (metadata) from the API, returning a
// publication if one was found, or `null` if there was no publication found.
const fetchMetadata = async path => {
    let data = await Ajax.fetch(path);
    let json = await data.json();

    if (!json) return null;

    return new Publication(json);
};

// Given the supplied configuration (API paths), load a publication from the
// Dimensions API. `callback` will be called when the load has completed,
// regardless of whether or not the publication exists.
//
// After the publication has been loaded, proceed to fetch citation data and
// metrics from the API.
export const loadPublicationFromAPI = async (config, callback = () => {}) => {
    const publication = await fetchMetadata(config.metadataPath);
    callback(publication);

    WindowManager.updatePageTitle(publication);
    WindowManager.updateCanonicalURL(config.rootURL, publication);

    if (publication) {
        publication.fetchCitations(config.citationsPath).then(() => callback(publication));
        publication.fetchMetrics(config.metricsPath).then(() => callback(publication));

        publication.dimensionsRoot = config.dimensionsPath;
    }

    return publication;
};
